import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { HandlingTierRowProps } from 'src/features/types/r2fTypes';

/**
 * Component for displaying a node row in the R2F threshold table
 * This represents an additional (non-default) node for a production capability
 */
export const R2FNodeRow: React.FC<HandlingTierRowProps> = ({
    node,
    productionCapabilityId,
    productionCapabilityName,
    pendingChange,
    onValueChange,
    onDelete,
    userCanEdit,
    warehouseId
}) => {
    // Check if this is a newly added node
    const isNewlyAdded = pendingChange && pendingChange.action === 'Add';
    
    // Check if this node belongs to Default_Network_PC and if editing should be restricted
    const isDefaultNetworkPC = productionCapabilityName === 'Default_Network_PC';
    
    // Default_Network_PC thresholds can only be edited when on the DEFAULT warehouse page
    // For all other production capabilities, editing is allowed based on userCanEdit permissions
    // This ensures FC-specific pages cannot modify Default_Network_PC threshold values
    const canEditDefaultNetworkPC = !isDefaultNetworkPC || (warehouseId === 'DEFAULT');
    
    // Determine effective userCanEdit based on restrictions
    const effectiveUserCanEdit = userCanEdit && canEditDefaultNetworkPC;
    
    // State for editing thresholds - auto-edit mode for newly added nodes
    const [isEditing, setIsEditing] = useState(isNewlyAdded);
    
    // Get current threshold values as strings, using pendingChange if available
    const [lowThresholdStr, setLowThresholdStr] = useState(
        (pendingChange ? pendingChange.lowThreshold : (node.lowThreshold ?? 0)).toString()
    );
    const [highThresholdStr, setHighThresholdStr] = useState(
        (pendingChange ? pendingChange.highThreshold : (node.highThreshold ?? 100)).toString()
    );
    
    // Compute numeric values for validation and saving
    const lowThreshold = lowThresholdStr === '' ? 0 : Number(lowThresholdStr);
    const highThreshold = highThresholdStr === '' ? 100 : Number(highThresholdStr);
    
    // Update state when pendingChange changes
    useEffect(() => {
        if (pendingChange) {
            setLowThresholdStr(pendingChange.lowThreshold.toString());
            setHighThresholdStr(pendingChange.highThreshold.toString());
        } else {
            setLowThresholdStr((node.lowThreshold ?? 0).toString());
            setHighThresholdStr((node.highThreshold ?? 100).toString());
        }
    }, [pendingChange, node.lowThreshold, node.highThreshold]);
    
    // Determine the row class based on pending changes
    const rowClass = useMemo(() => {
        if (!pendingChange) return '';
        
        switch (pendingChange.action) {
            case 'Add': return 'add-pending';
            case 'Modify': return 'modify-pending';
            case 'Delete': return 'delete-pending';
            default: return '';
        }
    }, [pendingChange]);
    
    // Format date
    const formatDate = useCallback((timestamp?: number) => {
        if (!timestamp) return '-';
        return new Date(timestamp).toLocaleString();
    }, []);
    
    // Handle edit button click
    const handleEditClick = useCallback(() => {
        setIsEditing(true);
        // Load values from pendingChange if it exists, otherwise use node values with fallbacks
        setLowThresholdStr((pendingChange ? pendingChange.lowThreshold : (node.lowThreshold ?? 0)).toString());
        setHighThresholdStr((pendingChange ? pendingChange.highThreshold : (node.highThreshold ?? 100)).toString());
    }, [pendingChange, node.lowThreshold, node.highThreshold]);
    
    // Handle save button click
    const handleSaveClick = useCallback(() => {
        const action = isNewlyAdded ? 'Add' : 'Modify';
        onValueChange(
            productionCapabilityId,
            node.id,
            lowThreshold ?? 0,
            highThreshold ?? 100,
            action
        );
        setIsEditing(false);
    }, [productionCapabilityId, node.id, lowThreshold, highThreshold, isNewlyAdded, onValueChange]);
    
    // Handle cancel button click
    const handleCancelClick = useCallback(() => {
        if (isNewlyAdded && onDelete) {
            onDelete(productionCapabilityId, node.id);
            return;
        }
        setIsEditing(false);
    }, [isNewlyAdded, onDelete, productionCapabilityId, node.id]);
    
    // Handle delete button click
    const handleDeleteClick = useCallback(() => {
        if (onDelete) {
            onDelete(productionCapabilityId, node.id);
        }
    }, [onDelete, productionCapabilityId, node.id]);

    // Handle revert click for pending changes
    const handleRevertClick = useCallback(() => {
        onValueChange(
            productionCapabilityId,
            node.id,
            0,  // Not used when action is NoChange
            0,  // Not used when action is NoChange
            'NoChange'
        );
    }, [productionCapabilityId, node.id, onValueChange]);
    
    // Determine if save button should be disabled
    const isSaveDisabled = (lowThreshold ?? 0) >= (highThreshold ?? 100) || (lowThreshold ?? 0) < 0;
    
    // Determine if this is a pending deletion
    const isPendingDeletion = pendingChange?.action === 'Delete';
    
    // Create edit actions based on current state
    const editActions = isEditing ? (
        <>
            <button
                className="save-button"
                onClick={handleSaveClick}
                disabled={isSaveDisabled}
                title="Save changes to this handling tier"
            >
                Save
            </button>
            <button
                className="cancel-button"
                onClick={handleCancelClick}
                title="Cancel editing"
            >
                Cancel
            </button>
        </>
    ) : (
        <div className="action-container">
            {/* Show edit button - restricted for Default_Network_PC on non-DEFAULT warehouses */}
            {(!pendingChange || pendingChange.action !== 'Delete') && (
                <button
                    className="edit-button"
                    onClick={handleEditClick}
                    disabled={isPendingDeletion || !effectiveUserCanEdit}
                    title={effectiveUserCanEdit ? "Edit this handling tier" : "Default_Network_PC can only be edited on the DEFAULT warehouse page"}
                >
                    Edit
                </button>
            )}
            {/* Show delete button - restricted for Default_Network_PC on non-DEFAULT warehouses */}
            <button
                className="delete-tier-button"
                onClick={handleDeleteClick}
                disabled={isPendingDeletion || !effectiveUserCanEdit}
                aria-label={`${isPendingDeletion ? 'Cancel delete' : 'Delete'} ${node.handlingTier} handling tier`}
                title={!effectiveUserCanEdit ? "Default_Network_PC can only be edited on the DEFAULT warehouse page" : (isPendingDeletion ? "Cancel deletion" : "Delete this handling tier")}
            >
                {isPendingDeletion ? '↩️' : '🗑️'}
            </button>
            {pendingChange && (
                <button
                    className="revert-button"
                    onClick={handleRevertClick}
                    disabled={!effectiveUserCanEdit}
                    title={effectiveUserCanEdit ? "Revert changes" : "Default_Network_PC can only be edited on the DEFAULT warehouse page"}
                >
                    Revert
                </button>
            )}
        </div>
    );
    
    return (
        <tr className={`node-row ${rowClass}`}>
            <td></td>
            <td>{productionCapabilityName}</td>
            <td>
                {node.handlingTier}
                {node.isDefault && <span className="default-badge"> (Default)</span>}
            </td>
            <td className="threshold-cell">
                {isEditing ? (
                    <input
                        type="text"
                        value={lowThresholdStr}
                        onChange={e => setLowThresholdStr(e.target.value)}
                        style={{ width: '60px' }}
                    />
                ) : (
                    pendingChange ? pendingChange.lowThreshold : 
                    (node.lowThreshold !== undefined ? node.lowThreshold : '-')
                )}
            </td>
            <td className="threshold-cell">
                {isEditing ? (
                    <input
                        type="text"
                        value={highThresholdStr}
                        onChange={e => setHighThresholdStr(e.target.value)}
                        style={{ width: '60px' }}
                    />
                ) : (
                    pendingChange ? pendingChange.highThreshold : 
                    (node.highThreshold !== undefined ? node.highThreshold : '-')
                )}
            </td>
            <td>{formatDate(node.publishDateEpochMillis)}</td>
            <td>{node.userAlias || '-'}</td>
            {userCanEdit && <td className="actions-cell">{editActions}</td>}
        </tr>
    );
};
